import { OpenInFullRounded } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { AgentLogo } from "components/KBo/Branding/AgentLogo";
import type { KboFlowResearchPreview } from "services/AcxAgentService";

function formatScope(preview?: KboFlowResearchPreview) {
    if (!preview) return undefined;

    const number = new Intl.NumberFormat();
    const scope: string[] = [];

    if (preview.totalConversationsInWindow !== undefined) {
        scope.push(
            `${number.format(
                preview.totalConversationsInWindow,
            )} conversations`,
        );
    }

    if (preview.window) scope.push(preview.window);

    if (preview.findingCount !== undefined) {
        scope.push(
            `${number.format(preview.findingCount)} finding${
                preview.findingCount === 1 ? "" : "s"
            }`,
        );
    }

    return scope.length > 0 ? scope.join(" · ") : undefined;
}

export function KboFlowResearchDrawerPreview({
    preview,
    onOpenFullscreen,
}: {
    preview?: KboFlowResearchPreview;
    onOpenFullscreen: () => void;
}) {
    const scope = formatScope(preview);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box>
                <Typography
                    component="p"
                    sx={{
                        color: "text.primary",
                        fontSize: 14,
                        fontWeight: 500,
                    }}
                >
                    Kbo Flow available:
                </Typography>
                {preview?.question && (
                    <Typography
                        component="p"
                        sx={{
                            color: "text.primary",
                            fontSize: 14,
                            fontWeight: 700,
                            lineHeight: "20px",
                            marginTop: 1,
                        }}
                    >
                        {preview.question}
                    </Typography>
                )}
                {scope && (
                    <Typography
                        component="p"
                        sx={{
                            color: "text.secondary",
                            fontSize: 12,
                            lineHeight: "18px",
                            marginTop: 0.5,
                        }}
                    >
                        {scope}
                    </Typography>
                )}
            </Box>
            <Box
                sx={(theme) => ({
                    background:
                        "linear-gradient(135deg, rgba(50, 209, 183, 0.08), rgba(52, 155, 199, 0.04))",
                    border: `1px solid ${theme.palette.primary[200]}`,
                    borderRadius: 2,
                    padding: 1.5,
                })}
            >
                <Box sx={{ alignItems: "center", display: "flex", gap: 0.75 }}>
                    <AgentLogo gradient height="16px" width="16px" />
                    <Typography
                        sx={(theme) => ({
                            color: theme.palette.primary[600],
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: "0.12em",
                        })}
                    >
                        KBO RESEARCH FINDING
                    </Typography>
                </Box>
                <Typography
                    component="p"
                    sx={{ fontSize: 14, lineHeight: "20px", marginTop: 1 }}
                >
                    <strong>There&apos;s more to see here.</strong> Open the
                    full view for the charts, quotes, and expanded analysis.
                </Typography>
            </Box>
            <Button
                onClick={onOpenFullscreen}
                size="small"
                startIcon={<OpenInFullRounded />}
                sx={{ alignSelf: "flex-start", textTransform: "none" }}
                variant="outlined"
            >
                Open full view
            </Button>
        </Box>
    );
}

export { formatScope as formatKboFlowResearchScope };
